// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, CircularProgress, Alert, Fade, Grow, Snackbar } from '@mui/material';
import {
  People as PeopleIcon,
  MonetizationOn as MoneyIcon,
  WifiTethering as WifiIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Dns as DnsIcon,
  Refresh as RefreshIcon,
  AccessTime as ClockIcon,
  CalendarToday as CalendarIcon,
  PersonAdd as PersonAddIcon,
  AddCircle as AddCircleIcon,
  LocalAtm as AtmIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Custom colors for a more vibrant dashboard
const colors = {
  blue: '#3b82f6',
  green: '#10b981',
  yellow: '#f59e0b',
  red: '#ef4444',
  purple: '#8b5cf6',
  skyBlue: '#0284c7'
};

const formatBytes = (bytes) => {
  if (!bytes || isNaN(bytes) || bytes <= 0) return '0 GB';
  const tb = bytes / (1024 * 1024 * 1024 * 1024);
  if (tb >= 1) return `${tb.toFixed(2)} TB`;
  const gb = bytes / (1024 * 1024 * 1024);
  return `${gb.toFixed(2)} GB`;
};

const getSpanishMonthName = () => {
  const months = [
    'ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO',
    'JULIO', 'AGOSTO', 'SEPTIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'
  ];
  return months[new Date().getMonth()];
};

// Componente para las sub-tarjetas planas y limpias del panel
const DashboardSubCard = ({ value, label, icon: Icon, color }) => (
  <Paper 
    elevation={0}
    sx={{ 
      p: 2, 
      mb: 1.5, 
      border: '1px solid #e5e7eb', 
      borderRadius: '6px', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between',
      backgroundColor: '#fff',
      transition: 'all 0.2s',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        borderColor: '#d1d5db'
      }
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h5" sx={{ fontWeight: '700', color: color, lineHeight: 1.1, fontSize: '1.65rem' }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: '600', display: 'block', mt: 0.5, fontSize: '10px', letterSpacing: '0.02em' }}>
        {label}
      </Typography>
    </Box>
    <Box sx={{ color: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon sx={{ fontSize: 24 }} />
    </Box>
  </Paper>
);

const Dashboard = () => {
  const [stats, setStats] = useState({
    payments: {
      today: { total: 0, count: 0 },
      pending: { total: 0, count: 0 },
      month: { total: 0, count: 0 }
    },
    clients: {
      today: 0,
      month: 0,
      total: 0
    },
    tickets: {
      today: 0,
      pending: 0,
      month: 0
    }
  });
  const [monthlyData, setMonthlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados del Centro de Comando de Red en Vivo
  const [routers, setRouters] = useState([]);
  const [syncSummary, setSyncSummary] = useState({ pending: 0, failed: 0, completed: 0, processing: 0 });
  const [syncLoading, setSyncLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const response = await fetch('/api/dashboard/stats', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setStats(data.stats || {
            payments: {
              today: { total: 0, count: 0 },
              pending: { total: 0, count: 0 },
              month: { total: 0, count: 0 }
            },
            clients: {
              today: 0,
              month: 0,
              total: 0
            },
            tickets: {
              today: 0,
              pending: 0,
              month: 0
            }
          });
          setMonthlyData(data.monthlyData || []);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    const fetchNetworkStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        const [healthRes, syncRes] = await Promise.all([
          fetch('/api/routers/health-status', { headers: { 'Authorization': `Bearer ${token}` } }),
          fetch('/api/routers/sync-queue/summary', { headers: { 'Authorization': `Bearer ${token}` } })
        ]);

        if (healthRes.ok) {
          const healthData = await healthRes.json();
          setRouters(healthData);
        }
        if (syncRes.ok) {
          const syncData = await syncRes.json();
          setSyncSummary(syncData);
        }
      } catch (err) {
        console.error('Error fetching live network status:', err);
      }
    };

    const loadAll = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('No se encontró token de autenticación');
        setLoading(false);
        return;
      }
      setLoading(true);
      await Promise.all([fetchDashboardData(), fetchNetworkStatus()]);
      setLoading(false);
    };

    loadAll();

    // Configurar polling en tiempo real cada 25 segundos
    const interval = setInterval(() => {
      fetchNetworkStatus();
    }, 25000);

    return () => clearInterval(interval);
  }, []);

  const handleRetryAllSync = async () => {
    try {
      setSyncLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/routers/sync-queue/retry-all', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Error al iniciar la sincronización');
      
      setToast({
        open: true,
        message: 'Cola de sincronización MikroTik iniciada exitosamente en segundo plano.',
        severity: 'success'
      });
      
      // Consultar estado de la cola tras 2 segundos de retardo
      setTimeout(async () => {
        const syncRes = await fetch('/api/routers/sync-queue/summary', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (syncRes.ok) {
          const syncData = await syncRes.json();
          setSyncSummary(syncData);
        }
      }, 2000);
    } catch (err) {
      setToast({
        open: true,
        message: err.message || 'No se pudo conectar con el servidor',
        severity: 'error'
      });
    } finally {
      setSyncLoading(false);
    }
  };

  return (
    <div className="p-6">
      <Typography variant="h4" className="mb-6 font-bold text-gray-800">
        Dashboard
      </Typography>

      {/* Show error message if any */}
      {error && (
        <Fade in={true}>
          <Alert
            severity="error"
            className="mb-4"
            action={
              <Box onClick={() => setError(null)} className="cursor-pointer">
                <ErrorIcon />
              </Box>
            }
          >
            {error}
          </Alert>
        </Fade>
      )}

      {/* Show loading state or content */}
      {loading ? (
        <Box className="flex flex-col items-center justify-center min-h-[400px]">
          <CircularProgress size={60} style={{ color: colors.blue }} />
          <Typography className="mt-4 text-gray-600">
            Cargando datos del dashboard...
          </Typography>
        </Box>
      ) : (
        <>
          {/* Estadísticas principales */}
          <Grid container spacing={3} className="mb-8">
            {/* Columna 1: Pagos Internet */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MoneyIcon style={{ color: '#475569' }} /> Pagos Internet
                </Typography>
              </Box>
              <DashboardSubCard
                value={`S/ ${stats.payments.today.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                label={`HOY - ${stats.payments.today.count} PAGOS`}
                icon={AtmIcon}
                color={colors.green}
              />
              <DashboardSubCard
                value={`S/ ${stats.payments.pending.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                label={`PENDIENTE - ${stats.payments.pending.count} PAGOS`}
                icon={ClockIcon}
                color={colors.yellow}
              />
              <DashboardSubCard
                value={`S/ ${stats.payments.month.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                label={`${getSpanishMonthName()} - ${stats.payments.month.count} PAGOS`}
                icon={CalendarIcon}
                color={colors.blue}
              />
            </Grid>

            {/* Columna 2: Clientes */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PeopleIcon style={{ color: '#475569' }} /> Clientes
                </Typography>
              </Box>
              <DashboardSubCard
                value={stats.clients.today}
                label="HOY"
                icon={PersonAddIcon}
                color={colors.green}
              />
              <DashboardSubCard
                value={stats.clients.month}
                label={getSpanishMonthName()}
                icon={CalendarIcon}
                color={colors.blue}
              />
              <DashboardSubCard
                value={stats.clients.total}
                label="TOTAL"
                icon={PeopleIcon}
                color={colors.skyBlue}
              />
            </Grid>

            {/* Columna 3: Tickets */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: '700', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TicketIcon style={{ color: '#475569' }} /> Tickets
                </Typography>
              </Box>
              <DashboardSubCard
                value={stats.tickets.today}
                label="HOY"
                icon={AddCircleIcon}
                color={colors.red}
              />
              <DashboardSubCard
                value={stats.tickets.pending}
                label="PENDIENTES"
                icon={ClockIcon}
                color={colors.yellow}
              />
              <DashboardSubCard
                value={stats.tickets.month}
                label={getSpanishMonthName()}
                icon={CalendarIcon}
                color={colors.green}
              />
            </Grid>
          </Grid>

          {/* Centro de Control de Red y Sincronización MikroTik */}
          <Grow in={true} timeout={600}>
            <Box className="mb-8">
              <Typography variant="h6" className="mb-4 font-bold text-gray-800 flex items-center space-x-2">
                <DnsIcon className="text-blue-500" style={{ fontSize: 22 }} />
                <span>Estado de la Red e Infraestructura (Live)</span>
              </Typography>
              <Grid container spacing={3}>
                {routers.length === 0 ? (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper 
                        className="p-4 text-center text-gray-500 border border-gray-100 flex flex-col items-center justify-center animate-pulse"
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '12px',
                          minHeight: '172px',
                          height: '100%'
                        }}
                      >
                        <CircularProgress size={20} className="mb-2" style={{ color: colors.blue }} />
                        <Typography variant="body2" className="text-gray-500 text-xs">
                          Buscando router activo...
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <Paper 
                        className="p-4 text-center text-gray-500 border border-gray-100 flex flex-col items-center justify-center animate-pulse"
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '12px',
                          minHeight: '172px',
                          height: '100%'
                        }}
                      >
                        <CircularProgress size={20} className="mb-2" style={{ color: colors.blue }} />
                        <Typography variant="body2" className="text-gray-500 text-xs">
                          Buscando router activo...
                        </Typography>
                      </Paper>
                    </Grid>
                  </>
                ) : (
                  routers.map((router) => (
                    <Grid item xs={12} sm={6} md={4} key={router.id}>
                      <Paper 
                        elevation={1} 
                        className="p-4 transition-all duration-300 hover:shadow-md border border-gray-100 flex flex-col justify-between"
                        style={{
                          background: 'rgba(255, 255, 255, 0.7)',
                          backdropFilter: 'blur(10px)',
                          borderRadius: '12px',
                          minHeight: '172px',
                          height: '100%'
                        }}
                      >
                        {/* Cabecera: Nombre y LED */}
                        <Box className="flex items-center justify-between">
                          <Box className="flex items-center space-x-2.5">
                            <Box className="relative flex items-center justify-center">
                              <Box 
                                className="rounded-full"
                                style={{
                                  backgroundColor: router.status === 'online' ? '#10b981' : '#ef4444',
                                  boxShadow: router.status === 'online' 
                                    ? '0 0 10px #10b981, inset 0 0 3px #047857' 
                                    : '0 0 10px #ef4444, inset 0 0 3px #b91c1c',
                                  width: '10px',
                                  height: '10px',
                                  animation: 'pulse 1.8s infinite alternate'
                                }}
                              />
                            </Box>
                            <Typography variant="subtitle2" className="font-bold text-gray-800 leading-tight">
                              {router.name}
                            </Typography>
                          </Box>
                          <Box className="rounded px-1.5 py-0.5 bg-blue-50 border border-blue-100 flex items-center space-x-1">
                            <DnsIcon style={{ fontSize: 10, color: colors.blue }} />
                            <span className="text-[9px] font-semibold text-blue-600">MIKROTIK</span>
                          </Box>
                        </Box>

                        {/* Medio: IP y Puerto */}
                        <Box className="my-2">
                          <Typography variant="body2" className="text-gray-500 font-mono text-[11px] bg-gray-50 px-2 py-1 rounded inline-block">
                            {router.ip}:{router.port}
                          </Typography>
                        </Box>

                        {/* Tráfico del Router: Download & Upload */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #f0f0f0', pt: 1.5, pb: 1, mt: 1 }}>
                          <Box>
                            <Typography variant="caption" className="text-gray-400 block text-[9px] leading-none mb-1">
                              Download Total
                            </Typography>
                            <span className="text-[10px] font-bold text-blue-600">
                              {formatBytes(router.totalDownload)}
                            </span>
                          </Box>
                          <Box className="text-right">
                            <Typography variant="caption" className="text-gray-400 block text-[9px] leading-none mb-1">
                              Upload Total
                            </Typography>
                            <span className="text-[10px] font-bold text-emerald-600">
                              {formatBytes(router.totalUpload)}
                            </span>
                          </Box>
                        </Box>

                        {/* Pie: Estado y CPE Clientes */}
                        <Box className="flex items-end justify-between border-t border-gray-50 pt-2 mt-auto">
                          <Box>
                            <Typography variant="caption" className="text-gray-400 block text-[9px] leading-none">
                              Estado
                            </Typography>
                            <span className={`text-[10px] font-bold ${router.status === 'online' ? 'text-emerald-600' : 'text-red-500'}`}>
                              {router.status === 'online' ? 'Conectado' : 'Sin Acceso'}
                            </span>
                          </Box>
                          <Box className="text-right">
                            <Typography variant="h6" className="font-bold text-gray-700 leading-none">
                              {router.clientCount}
                            </Typography>
                            <Typography variant="caption" className="text-gray-400 block text-[9px] mt-0.5">
                              Clientes CPE
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  ))
                )}

                {/* Sincronización MikroTik */}
                <Grid item xs={12} sm={12} md={4}>
                  <Paper 
                    elevation={1} 
                    className="p-4 transition-all duration-300 hover:shadow-md border border-gray-100 flex flex-col justify-between"
                    style={{
                      background: 'rgba(255, 255, 255, 0.7)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '12px',
                      minHeight: '142px',
                      height: '100%'
                    }}
                  >
                    {/* Cabecera */}
                    <Box className="flex items-center justify-between">
                      <Typography variant="subtitle2" className="font-bold text-gray-800 leading-tight">
                        Sincronización MikroTik
                      </Typography>
                      <Box className="flex items-center space-x-1.5">
                        {syncSummary.pending > 0 ? (
                          <Box className="rounded-full w-2 h-2 bg-amber-500" style={{ animation: 'pulse 1.2s infinite alternate' }} />
                        ) : (
                          <Box className="rounded-full w-2 h-2 bg-emerald-500" />
                        )}
                      </Box>
                    </Box>
                    
                    {/* Métricas */}
                    <Grid container spacing={1} className="my-1.5 text-center">
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" className="font-bold text-amber-500 leading-none">
                          {syncSummary.pending}
                        </Typography>
                        <span className="text-[9px] text-gray-400 block mt-0.5">Pendientes</span>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" className="font-bold text-red-500 leading-none">
                          {syncSummary.failed}
                        </Typography>
                        <span className="text-[9px] text-gray-400 block mt-0.5">Fallidas</span>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography variant="subtitle2" className="font-bold text-emerald-500 leading-none">
                          {syncSummary.completed}
                        </Typography>
                        <span className="text-[9px] text-gray-400 block mt-0.5">Sincronizadas</span>
                      </Grid>
                    </Grid>

                    {/* Botón de Reintento */}
                    <Box className="border-t border-gray-50 pt-2 mt-auto">
                      <button
                        onClick={handleRetryAllSync}
                        disabled={syncLoading || (syncSummary.pending === 0 && syncSummary.failed === 0)}
                        className={`w-full py-1.5 px-3 rounded-lg font-semibold text-[11px] transition-all duration-300 flex items-center justify-center space-x-1.5 border ${
                          syncLoading || (syncSummary.pending === 0 && syncSummary.failed === 0)
                            ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white border-blue-600 hover:shadow-md cursor-pointer'
                        }`}
                      >
                        {syncLoading ? (
                          <>
                            <CircularProgress size={10} color="inherit" />
                            <span>Sincronizando...</span>
                          </>
                        ) : (
                          <>
                            <RefreshIcon style={{ fontSize: 12 }} />
                            <span>Reintentar Cola</span>
                          </>
                        )}
                      </button>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          </Grow>

          {/* Gráfico de ingresos mensuales mejorado */}
          <Grow in={true} timeout={800}>
            <Paper className="p-4 mb-8 hover:shadow-lg transition-all duration-300">
              <Typography variant="h6" className="mb-4 font-semibold text-gray-800">
                Ingresos Mensuales
              </Typography>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={monthlyData}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 20,
                      bottom: 10,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="month"
                      stroke="#64748b"
                      tick={{ fill: '#64748b' }}
                    />
                    <YAxis
                      stroke="#64748b"
                      tick={{ fill: '#64748b' }}
                      tickFormatter={(value) => `S/ ${value}`}
                    />
                    <Tooltip
                      formatter={(value) => [`S/ ${value.toLocaleString()}`, 'Ingresos']}
                      contentStyle={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        border: `1px solid ${colors.blue}20`
                      }}
                    />
                    <Legend />
                    <defs>
                      <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={colors.blue} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={colors.blue} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="income"
                      stroke={colors.blue}
                      strokeWidth={3}
                      fill="url(#colorIncome)"
                      activeDot={{ r: 8, fill: colors.blue, stroke: '#fff', strokeWidth: 2 }}
                      name="Ingresos"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grow>

          {/* Animación global CSS */}
          <style>{`
            @keyframes pulse {
              0% { opacity: 0.6; transform: scale(0.95); }
              100% { opacity: 1; transform: scale(1.05); }
            }
          `}</style>

          {/* Notificaciones flotantes Toast */}
          <Snackbar
            open={toast.open}
            autoHideDuration={6000}
            onClose={() => setToast({ ...toast, open: false })}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert 
              onClose={() => setToast({ ...toast, open: false })} 
              severity={toast.severity} 
              sx={{ width: '100%', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
            >
              {toast.message}
            </Alert>
          </Snackbar>
        </>
      )}
    </div>
  );
};

export default Dashboard;